/**
 * "Corrigir e-mail" (pedido do Vitor, 06/10: o cliente digitou o e-mail da compra errado, "gmai.com", e a equipe nao tinha
 * como corrigir): acrescenta o certo, torna principal, remove o errado e reenvia o acesso. Cada passo e uma rota que
 * ja existe e e segura sozinha, entao a ordem e a regra: NUNCA apaga antes de o novo existir, e se um passo falha para
 * ali, sem desfazer nada, e diz o que ficou feito. Tentar de novo pula o que ja foi feito.
 *
 * Aqui so a sequencia, sem tela e sem fetch (as chamadas entram por parametro), pra provar em teste cada ponto de falha.
 */

export type Passo = "adicionar" | "principal" | "remover" | "reenviar";

/** O que a tela escreve de cada passo, no infinitivo, como numa lista de tarefas. */
export const TITULO_DO_PASSO: Record<Passo, string> = {
  adicionar: "Acrescentar o e-mail certo",
  principal: "Torná-lo o e-mail de acesso",
  remover: "Remover o e-mail errado",
  reenviar: "Reenviar o acesso pro e-mail certo",
};

/**
 * Os passos desta correcao. Tornar principal so entra se o e-mail errado era o principal: corrigir um e-mail
 * secundario nao mexe no de acesso. Reenviar so se a equipe marcou.
 */
export function passosDaCorrecao(o: { antigoEraPrincipal: boolean; reenviar: boolean }): Passo[] {
  return ["adicionar", ...(o.antigoEraPrincipal ? (["principal"] as const) : []), "remover", ...(o.reenviar ? (["reenviar"] as const) : [])];
}

export type Chamadas = {
  /** O id do e-mail nesta pessoa, lido de novo agora, ou nulo. Evita acrescentar em dobro quando a resposta se perdeu. */
  achar: (email: string) => Promise<string | null>;
  adicionar: (email: string, confirmadoComACliente: boolean) => Promise<string>;
  principal: (emailId: string) => Promise<void>;
  remover: (emailId: string) => Promise<void>;
  reenviar: (emailId: string) => Promise<void>;
};

/** O que ja foi feito. Guardado entre uma tentativa e outra: "tentar de novo" continua de onde parou. */
export type Andamento = { novoId?: string; jaEstava?: boolean; feitos: Passo[] };
export type Parada = { passo: Passo; erro: string };

export const andamentoVazio = (): Andamento => ({ feitos: [] });

export async function correr(
  passos: Passo[],
  entrada: { email: string; confirmadoComACliente: boolean; antigoId: string },
  chamadas: Chamadas,
  anterior: Andamento,
  descrever: (erro: unknown) => string,
  aoMudar?: (a: Andamento, atual: Passo | null) => void,
): Promise<{ andamento: Andamento; parou: Parada | null }> {
  const a: Andamento = { ...anterior, feitos: [...anterior.feitos] };
  for (const passo of passos) {
    if (a.feitos.includes(passo)) continue;
    aoMudar?.(a, passo);
    try {
      if (passo === "adicionar") {
        // O e-mail certo ja pode estar na conta (um secundario, ou uma tentativa que a resposta nao voltou): usa o que ha.
        const existente = a.novoId ?? (await chamadas.achar(entrada.email));
        if (existente) {
          a.novoId = existente;
          a.jaEstava = !anterior.novoId;
        } else {
          a.novoId = await chamadas.adicionar(entrada.email, entrada.confirmadoComACliente);
        }
      } else if (passo === "principal") await chamadas.principal(a.novoId!);
      else if (passo === "remover") await chamadas.remover(entrada.antigoId);
      else await chamadas.reenviar(a.novoId!);
    } catch (erro) {
      aoMudar?.(a, null);
      return { andamento: a, parou: { passo, erro: descrever(erro) } };
    }
    a.feitos.push(passo);
  }
  aoMudar?.(a, null);
  return { andamento: a, parou: null };
}

/** A frase de quando para: onde, o que ficou feito e o que falta. Nunca diz que desfez nada, porque nada se desfaz. */
export function fraseDaParada(passos: Passo[], a: Andamento, p: Parada): string {
  const n = passos.indexOf(p.passo) + 1;
  const feitos = passos.filter((x) => a.feitos.includes(x));
  const faltam = passos.filter((x) => !a.feitos.includes(x));
  const lista = (l: Passo[]) => l.map((x) => TITULO_DO_PASSO[x].toLowerCase()).join("; ");
  return `Parou no passo ${n} de ${passos.length} (${TITULO_DO_PASSO[p.passo].toLowerCase()}). ${feitos.length ? `Já está feito: ${lista(feitos)}.` : "Nada foi feito ainda."} Falta: ${lista(faltam)}. Nada foi apagado antes de o e-mail certo existir.`;
}
